export const DEFAULT_EMBEDDING_MODEL = "onnx-community/granite-embedding-small-english-r2-ONNX";
export const DEFAULT_EMBEDDING_MODEL_REVISION = "1dc7835ba0cb9c76a3618d0bf0c427c97671b3c8";
export const DEFAULT_RERANKER_MODEL = "mixedbread-ai/mxbai-rerank-xsmall-v1";
export const HYBRID_CANDIDATE_LIMIT = 20;
export const HYBRID_RESULT_LIMIT = 8;
export const HYBRID_RERANK_LIMIT = 10;
export const HYBRID_RETRIEVAL_RUNTIME_ID = "hybrid-bm25-granite-rrf-v1";

const BGE_QUERY_PREFIX = "Represent this sentence for searching relevant passages: ";

export function formatEmbeddingQuery(model: string, query: string) {
  return model.toLowerCase().includes("granite-embedding") ? query : `${BGE_QUERY_PREFIX}${query}`;
}

export type HybridRetrievalChunk = {
  chunkId: string;
  documentId: string;
  chunkIndex: number;
  title: string;
  url: string;
  text: string;
  aliases: string[];
};

export type HybridRankedChunk = HybridRetrievalChunk & {
  score: number;
  bm25Score: number | null;
  denseScore: number | null;
  rerankerScore: number | null;
};

export type HybridRetrievalDiagnostics = {
  runtimeId: typeof HYBRID_RETRIEVAL_RUNTIME_ID;
  mode: "hybrid" | "bm25";
  semanticModel: string | null;
  rerankerModel: string | null;
  fallbackReason: string | null;
  corpusCacheKey: string;
  candidateCounts: {
    bm25: number;
    dense: number;
    merged: number;
    reranked: number;
  };
  durationsMs: {
    bm25: number;
    dense: number | null;
    rerank: number | null;
    total: number;
  };
};

export type HybridRetrievalResult = {
  chunks: HybridRankedChunk[];
  diagnostics: HybridRetrievalDiagnostics;
};

export type RetrievalProgress = {
  stage: "keyword" | "embedding" | "reranking" | "complete";
  progress: number;
  text: string;
};

export type SemanticEmbeddingProvider = {
  model: string;
  prepare?(passages: string[], options: {
    cacheKey: string;
    onProgress?: (progress: RetrievalProgress) => void;
    precomputedVectors?: number[][];
  }): Promise<void>;
  embed(texts: string[], options: {
    cacheKey: string;
    isCancelled: () => boolean;
    onProgress?: (progress: RetrievalProgress) => void;
  }): Promise<number[][]>;
};

export type RerankerProvider = {
  model: string;
  prepare?(options?: { onProgress?: (progress: RetrievalProgress) => void }): Promise<void>;
  score(query: string, passages: string[], options: {
    isCancelled: () => boolean;
    onProgress?: (progress: RetrievalProgress) => void;
  }): Promise<number[]>;
};

type ScoredIndex = { index: number; score: number };

const RRF_K = 60;
const TECHNICAL_TOKEN = /(?:--[a-z0-9-]+|\$\{[^}\s]+\}|\/[a-z0-9._~!$&'()*+,;=:@%\/-]+|[a-z0-9]+(?:[._:-][a-z0-9]+)+|[a-z0-9]+)/gi;

function now() {
  return typeof performance === "undefined" ? Date.now() : performance.now();
}

function tokenize(value: string) {
  return (value.normalize("NFKC").toLowerCase().match(TECHNICAL_TOKEN) || [])
    .flatMap((token) => {
      const components = token.split(/[._:/-]+/).filter((part) => part.length > 1);
      return components.length > 1 ? [token, ...components] : [token];
    });
}

export function hybridPassage(chunk: HybridRetrievalChunk) {
  const path = (() => {
    try {
      return new URL(chunk.url).pathname;
    } catch {
      return chunk.url;
    }
  })();
  return [chunk.title, path, ...chunk.aliases, chunk.text].filter(Boolean).join("\n");
}

function bm25(chunks: HybridRetrievalChunk[], query: string, limit: number) {
  const documents = chunks.map((chunk) => tokenize(hybridPassage(chunk)));
  const queryTerms = Array.from(new Set(tokenize(query)));
  if (queryTerms.length === 0) return [];
  const averageLength = documents.reduce((total, document) => total + document.length, 0)
    / Math.max(1, documents.length);
  const frequencies = new Map<string, number>();
  for (const document of documents) {
    for (const term of new Set(document)) {
      frequencies.set(term, (frequencies.get(term) || 0) + 1);
    }
  }
  const k1 = 1.2;
  const b = 0.75;
  return documents
    .map((document, index) => {
      const counts = new Map<string, number>();
      for (const term of document) counts.set(term, (counts.get(term) || 0) + 1);
      let score = 0;
      for (const term of queryTerms) {
        const frequency = counts.get(term) || 0;
        if (!frequency) continue;
        const documentFrequency = frequencies.get(term) || 0;
        const inverseDocumentFrequency = Math.log(
          1 + (chunks.length - documentFrequency + 0.5) / (documentFrequency + 0.5),
        );
        score += inverseDocumentFrequency
          * ((frequency * (k1 + 1))
            / (frequency + k1 * (1 - b + b * document.length / averageLength)));
      }
      return { index, score };
    })
    .filter((candidate) => candidate.score > 0)
    .sort((left, right) => right.score - left.score || left.index - right.index)
    .slice(0, limit);
}

function cosine(left: number[], right: number[]) {
  let dot = 0;
  let leftMagnitude = 0;
  let rightMagnitude = 0;
  const length = Math.min(left.length, right.length);
  for (let index = 0; index < length; index += 1) {
    dot += left[index] * right[index];
    leftMagnitude += left[index] * left[index];
    rightMagnitude += right[index] * right[index];
  }
  const denominator = Math.sqrt(leftMagnitude) * Math.sqrt(rightMagnitude);
  return denominator ? dot / denominator : 0;
}

function reciprocalRankFusion(rankings: ScoredIndex[][]) {
  const scores = new Map<number, number>();
  for (const ranking of rankings) {
    ranking.forEach((candidate, rank) => {
      scores.set(candidate.index, (scores.get(candidate.index) || 0) + 1 / (RRF_K + rank + 1));
    });
  }
  return Array.from(scores, ([index, score]) => ({ index, score }))
    .sort((left, right) => right.score - left.score || left.index - right.index);
}

function cancelledError() {
  return new DOMException("Retrieval cancelled", "AbortError");
}

export async function retrieveHybrid({
  chunks,
  query,
  corpusCacheKey,
  semantic,
  reranker,
  candidateLimit = HYBRID_CANDIDATE_LIMIT,
  resultLimit = HYBRID_RESULT_LIMIT,
  isCancelled = () => false,
  onProgress,
}: {
  chunks: HybridRetrievalChunk[];
  query: string;
  corpusCacheKey: string;
  semantic?: SemanticEmbeddingProvider;
  reranker?: RerankerProvider;
  candidateLimit?: number;
  resultLimit?: number;
  isCancelled?: () => boolean;
  onProgress?: (progress: RetrievalProgress) => void;
}): Promise<HybridRetrievalResult> {
  const started = now();
  onProgress?.({ stage: "keyword", progress: 0, text: "Searching exact terms" });
  const keywordStarted = now();
  const keyword = bm25(chunks, query, candidateLimit);
  const keywordDuration = now() - keywordStarted;
  if (isCancelled()) throw cancelledError();

  let dense: ScoredIndex[] = [];
  let denseDuration: number | null = null;
  let fallbackReason: string | null = semantic ? null : "Semantic embeddings are unavailable";
  if (semantic) {
    const denseStarted = now();
    try {
      onProgress?.({ stage: "embedding", progress: 0, text: "Searching by meaning" });
      const vectors = await semantic.embed(
        [query, ...chunks.map(hybridPassage)],
        { cacheKey: corpusCacheKey, isCancelled, onProgress },
      );
      if (isCancelled()) throw cancelledError();
      const queryVector = vectors[0];
      dense = vectors.slice(1)
        .map((vector, index) => ({ index, score: cosine(queryVector, vector) }))
        .filter((candidate) => Number.isFinite(candidate.score))
        .sort((left, right) => right.score - left.score || left.index - right.index)
        .slice(0, candidateLimit);
      denseDuration = now() - denseStarted;
    } catch (error) {
      if (isCancelled() || (error instanceof DOMException && error.name === "AbortError")) throw error;
      denseDuration = now() - denseStarted;
      fallbackReason = error instanceof Error ? error.message : "Semantic retrieval failed";
    }
  }

  const fused = dense.length > 0
    ? reciprocalRankFusion([keyword, dense]).slice(0, candidateLimit)
    : keyword;
  const keywordScores = new Map(keyword.map((candidate) => [candidate.index, candidate.score]));
  const denseScores = new Map(dense.map((candidate) => [candidate.index, candidate.score]));
  let rerankerScores = new Map<number, number>();
  let rerankDuration: number | null = null;
  if (reranker && fused.length > 0) {
    const rerankStarted = now();
    try {
      onProgress?.({ stage: "reranking", progress: 0, text: "Checking supporting passages" });
      const reranked = fused.slice(0, HYBRID_RERANK_LIMIT);
      const scores = await reranker.score(
        query,
        reranked.map((candidate) => hybridPassage(chunks[candidate.index])),
        { isCancelled, onProgress },
      );
      if (isCancelled()) throw cancelledError();
      rerankerScores = new Map(reranked.map((candidate, index) => [candidate.index, scores[index]]));
      fused.sort((left, right) =>
        Number(rerankerScores.has(right.index)) - Number(rerankerScores.has(left.index))
        || (rerankerScores.get(right.index) || 0) - (rerankerScores.get(left.index) || 0)
        || right.score - left.score
        || left.index - right.index);
      rerankDuration = now() - rerankStarted;
    } catch (error) {
      if (isCancelled() || (error instanceof DOMException && error.name === "AbortError")) throw error;
      rerankDuration = now() - rerankStarted;
      fallbackReason ||= error instanceof Error ? error.message : "Reranking failed";
    }
  }

  const selected = fused.slice(0, resultLimit).map((candidate) => ({
    ...chunks[candidate.index],
    score: rerankerScores.get(candidate.index) ?? candidate.score,
    bm25Score: keywordScores.get(candidate.index) ?? null,
    denseScore: denseScores.get(candidate.index) ?? null,
    rerankerScore: rerankerScores.get(candidate.index) ?? null,
  }));
  const diagnostics: HybridRetrievalDiagnostics = {
    runtimeId: HYBRID_RETRIEVAL_RUNTIME_ID,
    mode: dense.length > 0 ? "hybrid" : "bm25",
    semanticModel: dense.length > 0 && semantic ? semantic.model : null,
    rerankerModel: rerankerScores.size > 0 && reranker ? reranker.model : null,
    fallbackReason,
    corpusCacheKey,
    candidateCounts: {
      bm25: keyword.length,
      dense: dense.length,
      merged: fused.length,
      reranked: rerankerScores.size,
    },
    durationsMs: {
      bm25: keywordDuration,
      dense: denseDuration,
      rerank: rerankDuration,
      total: now() - started,
    },
  };
  onProgress?.({ stage: "complete", progress: 1, text: "Context ready" });
  return { chunks: selected, diagnostics };
}
